import { Component, Input, ViewEncapsulation } from '@angular/core';

import { SsrMarkToUnwrapDirective } from '../../directives/ssr-markto-unwrap.directive';
import type { WrappedValue } from '../../mailgun/wrap-value';
import { EmailMailgunHtmlComponent } from '../email-html/email-mailgun-html.component';

export type TextColor =
  | 'light'
  | 'default'
  | 'dark'
  | 'primary'
  | 'tellent'
  | 'disabled'
  | 'success';

type PresetSize = 'small' | 'medium' | 'large';
type ElementBehavior = 'inline' | 'block';

export interface TextPresetCompounds {
  size?: PresetSize;
  behavior?: ElementBehavior;
  vAlign?: 'baseline' | 'top' | 'middle' | 'bottom';
  bold?: boolean;
  color?: TextColor;
  strikethrough?: boolean;
}

const presets = <const>{
  inline: {
    size: 'medium',
    behavior: 'inline',
    vAlign: 'middle',
    bold: false,
    color: 'default',
  },
  paragraph: {
    size: 'medium',
    behavior: 'block',
    vAlign: 'top',
    bold: false,
    color: 'default',
  },
  header: {
    size: 'large',
    behavior: 'inline',
    vAlign: 'baseline',
    bold: true,
    color: 'default',
  },
};

export type TextPreset = keyof typeof presets;

const getPreset: (
  name: keyof typeof presets,
  mods?: Partial<TextPresetCompounds>,
) => TextPresetCompounds = (name, mods = {}) => {
  if (name in presets) {
    const base = presets[name];
    return { ...base, ...mods };
  } else {
    throw new Error(
      `TextPreset '${name}' not found.\nAvailable: ${Object.keys(presets).join(', ')}`,
    );
  }
};

const getTagForPreset = (presetName: string, preset: TextPresetCompounds) => {
  switch (presetName) {
    case 'inline':
      return 'span';

    case 'paragraph':
      return 'p';

    case 'header':
      switch (preset.size) {
        case 'medium':
          return 'h2';

        case 'small':
          return 'h3';

        case 'large':
        default:
          return 'h1';
      }

    default:
      return 'span';
  }
};

@Component({
  selector: 'email-text',
  styleUrls: ['./email-text.component.less'],
  hostDirectives: [SsrMarkToUnwrapDirective],
  template: `
    @switch (tag) {
      @case ('span') {
        <span [class]="classNames"><mailgun-html [html]="contents" /></span>
      }
      @case ('p') {
        <p [class]="classNames"><mailgun-html [html]="contents" /></p>
      }
      @case ('h1') {
        <h1 [class]="classNames"><mailgun-html [html]="contents" /></h1>
      }
      @case ('h2') {
        <h2 [class]="classNames"><mailgun-html [html]="contents" /></h2>
      }
      @case ('h3') {
        <h3 [class]="classNames"><mailgun-html [html]="contents" /></h3>
      }
    }
  `,
  imports: [EmailMailgunHtmlComponent],
  encapsulation: ViewEncapsulation.None,
})
export class EmailTextComponent implements TextPresetCompounds {
  @Input() public size?: PresetSize;
  @Input() public behavior?: ElementBehavior;
  @Input() public vAlign?: 'baseline' | 'top' | 'middle' | 'bottom';
  @Input() public bold?: boolean;
  @Input() public strikethrough?: boolean;
  @Input() public color?: TextColor;
  @Input() public className?: string;
  @Input() public presetName?: keyof typeof presets;
  @Input() public contents: WrappedValue<string | number>;

  public tag: ReturnType<typeof getTagForPreset>;
  public classNames: string[];

  public ngOnInit() {
    const { tag, classNames } = this.getStyling();
    this.tag = tag;
    this.classNames = classNames;
  }

  public getStyling(): {
    tag: ReturnType<typeof getTagForPreset>;
    classNames: string[];
  } {
    const optClassName = this.className || '';
    const defaultPreset = 'inline';
    const presetName = this.presetName;
    const presetMods = {
      ...(this.size ? { size: this.size } : {}),
      ...(this.behavior ? { behavior: this.behavior } : {}),
      ...(this.vAlign ? { vAlign: this.vAlign } : {}),
      ...(this.color ? { color: this.color } : {}),
      ...(this.bold ? { bold: this.bold } : {}),
      ...(this.strikethrough ? { strikethrough: this.strikethrough } : {}),
    };

    const preset = getPreset(presetName || defaultPreset, presetMods);
    const tag = getTagForPreset(presetName || defaultPreset, preset);

    return {
      tag,
      classNames: [
        `text`,
        `text-size-${preset.size}`,
        `text-behavior-${preset.behavior}`,
        `text-v-align-${preset.vAlign}`,
        `text-color-${preset.color}`,
        ...(preset.bold ? [`text-bold`] : []),
        ...(preset.strikethrough ? [`text-strikethrough`] : []),
        ...(optClassName ? [optClassName] : []),
      ],
    };
  }
}
